import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { trpc } from "@/lib/trpc";
import { UpdateProductSchema, type UpdateProductInput, type CustomFeatureItem } from "@/types/activation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { ProductFeatureInput, type FormCustomFeatureItem } from "./ProductFeatureInput";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

interface Feature {
  id: string;
  key: string;
  name: string;
  description?: string | null;
}

interface Product {
  id: string;
  name: string;
  description?: string | null;
  features?: Feature[]; // Standard features
  customFeatures?: Record<string, string | number | boolean> | CustomFeatureItem[] | FormCustomFeatureItem[];
  monthlyPrice?: number | null;
  yearlyPrice?: number | null;
  maxBindingsPerCode?: number | null;
}

interface EditProductDialogProps {
  product: Product;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onProductUpdated?: () => void;
}

export function EditProductDialog({ product, isOpen, onOpenChange, onProductUpdated }: EditProductDialogProps) {
  const { t } = useTranslation();

  const form = useForm<UpdateProductInput>({
    resolver: zodResolver(UpdateProductSchema),
    defaultValues: {
      id: product.id,
      name: product.name,
      description: product.description ?? null, // Align with schema allowing null
      monthlyPrice: product.monthlyPrice ?? null,
      yearlyPrice: product.yearlyPrice ?? null,
      customFeatures: [], // Initialize as empty array; useEffect will populate
      maxBindingsPerCode: product.maxBindingsPerCode ?? 1,
    },
  });

  useEffect(() => {
    if (product) {
      let processedCustomFeatures: FormCustomFeatureItem[] = [];

      if (product.customFeatures) {
        if (typeof product.customFeatures === 'object' && !Array.isArray(product.customFeatures)) {
          processedCustomFeatures = Object.entries(product.customFeatures).map(([key, value]) => {
            const valueType = typeof value === 'boolean' ? 'boolean' :
                              typeof value === 'number' ? 'number' : 'text';

            // Preserve existing ID if available (from previous form submission)
            const existingFeature = Array.isArray(form.getValues('customFeatures'))
              ? form.getValues('customFeatures').find(f => f.keyName === key)
              : null;

            return {
              id: existingFeature?.id || `feature-${key}-${valueType}`,
              keyName: key,
              value: value,
              valueType: valueType
            };
          });
        } else if (Array.isArray(product.customFeatures)) {
          processedCustomFeatures = product.customFeatures.map(feature => ({
            ...feature,
            id: feature.id || `feature-${feature.keyName}-${feature.valueType}`
          })) as FormCustomFeatureItem[];
        }
        console.log('Processed features before reset:', processedCustomFeatures);
      }

      form.reset({
        id: product.id,
        name: product.name,
        description: product.description ?? null, // Align with schema allowing null
        monthlyPrice: product.monthlyPrice ?? null,
        yearlyPrice: product.yearlyPrice ?? null,
        customFeatures: processedCustomFeatures,
        maxBindingsPerCode: product.maxBindingsPerCode ?? 1,
      });

      console.log("Processed Features:", processedCustomFeatures);
    }
  }, [product, form]);

  const utils = trpc.useUtils();
  const updateProductMutation = trpc.activation.updateProduct.useMutation({
    onSuccess: (data) => {
      toast.success(t("admin.activation.products.updateSuccess", { name: data.name }));
      utils.activation.listProducts.invalidate();
      utils.activation.getProduct.invalidate({ id: product.id });
      setTimeout(() => {
        onProductUpdated?.();
        onOpenChange(false);
      }, 300);
    },
    onError: (error) => {
      toast.error(t("admin.activation.products.updateError", { message: error.message }));
    },
  });

  const { data: featuresData, isLoading: isLoadingFeatures } = trpc.activation.listFeatures.useQuery();

  const onSubmit = async (data: UpdateProductInput) => {
    // Check which values have changed to optimize update payload
    const changedData: UpdateProductInput = { id: product.id };
    // Copy basic fields if changed
    if (data.name !== product.name) changedData.name = data.name;
    // Compare with (product.description ?? null) to handle initial undefined/null consistently
    if (data.description !== (product.description ?? null)) changedData.description = data.description;
    if (data.monthlyPrice !== (product.monthlyPrice ?? null)) changedData.monthlyPrice = data.monthlyPrice;
    if (data.yearlyPrice !== (product.yearlyPrice ?? null)) changedData.yearlyPrice = data.yearlyPrice;
    if (data.maxBindingsPerCode !== (product.maxBindingsPerCode ?? 1)) changedData.maxBindingsPerCode = data.maxBindingsPerCode;


    // Always include featureIds if it exists
    if (data.featureIds) changedData.featureIds = data.featureIds;

    // Handle customFeatures conversion between array and object formats
    if (data.customFeatures && Array.isArray(data.customFeatures)) {
      // Convert array to object format for comparison with database
      const currentFeaturesObj = data.customFeatures.reduce((acc, feature) => {
        if (feature.keyName) {
          acc[feature.keyName] = feature.value;
        }
        return acc;
      }, {} as Record<string, string | number | boolean>);

      // Get original features in object format
      const originalFeaturesObj = product.customFeatures && typeof product.customFeatures === 'object'
        ? product.customFeatures
        : Array.isArray(product.customFeatures)
          ? product.customFeatures.reduce((acc, feature) => {
              if (feature.keyName) {
                acc[feature.keyName] = feature.value;
              }
              return acc;
            }, {} as Record<string, string | number | boolean>)
          : {};

      console.log('Feature comparison:', {
        original: originalFeaturesObj,
        current: currentFeaturesObj
      });

      // Compare the objects
      if (JSON.stringify(currentFeaturesObj) !== JSON.stringify(originalFeaturesObj)) {
        // Convert back to array format for API (which will convert to object for DB)
        changedData.customFeatures = data.customFeatures.filter(cf => cf.keyName).map(cf => ({
          id: cf.id,
          keyName: cf.keyName,
          value: cf.value,
          valueType: cf.valueType,
        }));

        console.log('Custom features changed - sending update:', changedData.customFeatures);
      }
    } else if (!data.customFeatures && (
      (Array.isArray(product.customFeatures) && product.customFeatures.length > 0) ||
      (typeof product.customFeatures === 'object' && Object.keys(product.customFeatures).length > 0)
    )) {
      // All features were removed
      changedData.customFeatures = [];
      console.log('All custom features removed');
    }

    console.log("提交更新数据:", changedData); // 添加日志

    // 仅当有数据要更新时才进行更新
    if (Object.keys(changedData).length > 1) { // id加至少1个字段
      try {
        updateProductMutation.mutate(changedData);
      } catch (error) {
        console.error('更新产品失败:', error);
      }
    } else {
      // 如果没有字段更改，跳过更新并直接关闭
      toast.info(t("common.noChanges")); // This key was added to en.json
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t("admin.activation.products.editProductTitle")}</DialogTitle>
          <DialogDescription>
            {t("admin.activation.products.editProductDesc")}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-medium">{t("admin.activation.products.productName")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("admin.activation.products.productNamePlaceholder")}
                      {...field}
                      className="h-10"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-medium">{t("admin.activation.products.productDescription")}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t("admin.activation.products.productDescriptionPlaceholder")}
                      {...field}
                      value={field.value ?? ""}
                      className="min-h-24 resize-y"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* 价格输入字段 */}
            <div className="space-y-4">
              <h3 className="text-base font-medium">{t("admin.activation.products.pricing")}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="monthlyPrice"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("admin.activation.products.monthlyPrice")}</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              className="h-10 pl-7"
                              {...field}
                              onChange={(e) => {
                                const rawValue = e.target.value;
                                if (rawValue === "") {
                                  field.onChange(null);
                                } else {
                                  const numValue = Number.parseFloat(rawValue);
                                  field.onChange(Number.isNaN(numValue) ? null : numValue); // Or handle NaN differently if needed
                                }
                              }}
                              value={field.value ?? ""}
                            />
                          </div>
                        </FormControl>
                        <FormDescription className="text-xs">
                          {t("admin.activation.products.optional")}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="yearlyPrice"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("admin.activation.products.yearlyPrice")}</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              className="h-10 pl-7"
                              {...field}
                              onChange={(e) => {
                                const rawValue = e.target.value;
                                if (rawValue === "") {
                                  field.onChange(null);
                                } else {
                                  const numValue = Number.parseFloat(rawValue);
                                  field.onChange(Number.isNaN(numValue) ? null : numValue); // Or handle NaN differently if needed
                                }
                              }}
                              value={field.value ?? ""}
                            />
                          </div>
                        </FormControl>
                        <FormDescription className="text-xs">
                          {t("admin.activation.products.optional")}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            <FormField
              control={form.control}
              name="maxBindingsPerCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-medium">{t("admin.activation.products.domainBindingLimitLabel")}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      placeholder={t("admin.activation.products.domainBindingLimitPlaceholder")}
                      className="h-10"
                      {...field}
                      onChange={(e) => {
                        const rawValue = e.target.value;
                        if (rawValue === "") {
                          field.onChange(null);
                        } else {
                          const numValue = Number.parseInt(rawValue, 10);
                          if (Number.isNaN(numValue)) {
                            field.onChange(null);
                          } else {
                            field.onChange(numValue >= 0 ? numValue : 0);
                          }
                        }
                      }}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormDescription>
                    {t("admin.activation.products.domainBindingLimitDescription")}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="customFeatures"
              render={() => (
                <FormItem>
                  <FormLabel className="text-base font-medium">{t("admin.activation.products.features")}</FormLabel>
                  <div className="border rounded-md p-3 bg-card">
                    <ProductFeatureInput
                      control={form.control}
                      register={form.register}
                      errors={form.formState.errors}
                      setValue={form.setValue}
                      getValues={form.getValues}
                    />
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 pt-2">
              <DialogClose asChild>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  {t("common.cancel")}
                </Button>
              </DialogClose>
              <Button type="submit" disabled={updateProductMutation.isPending}>
                {updateProductMutation.isPending ? t("common.saving") : t("common.saveChanges")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}